import { Dialog, DialogActions, DialogContent, DialogTitle, List, ListItem, TablePagination } from "@mui/material";
import { StyledDataGrid } from "../../../../../../common/components/styled_data_grid";
import { useCallback, useEffect, useMemo } from "react";
import { AvaliacaoHabilidadeForm, AvaliacaoHabilidadeItemForm, AvaliacaoHabilidadeSchema } from "../../../schemas/Funcionarios_cadastro/habilidade_schema";
import { FormDatePickerInput } from "../../../../../../common/components/masked_inputs/react_forms/form_date_picker_input";
import { DefaultFormAutocomplete } from "../../../../../../common/components/default_form_autocomplete";
import { useForm } from "react-hook-form";
import { GenerateHabilidadeItemsColumnsDefinition } from "../columns/generate_habilidade_items";
import { HabilidadeItem } from "../../../../domain/models/habilidade_item";
import { FuncionarioDropDown } from "../../../../../../common/domain/models/funcionario_dropdown";
import { zodResolver } from "@hookform/resolvers/zod";
import { AvaliacaoHabilidade } from "../../../../domain/models/avaliacao_habilidade";
import { ModalLoading } from "../../../../../../common/components/modal_loading";
import { ModalContainer } from "../../../../../../common/components/modal_container";
import { ChangeLogData } from "../../../../../../common/domain/models/change_log_data";
import { LogTipoTabela } from "../../../../../../common/utils/log_tabela_tipo";
import { isNotNullOrUndefined } from "../../../../../../common/utils/utils";

import PrimaryButton from "../../../../../../common/components/primary_button";
import FormContainer from "../../../../../../common/components/form_container";
import DefaultFormInput from "../../../../../../common/components/default_form_input";
import DynamicIcon from "../../../../../../common/components/dynamic_icon";

const changeLogData: ChangeLogData = {
  createdDate: new Date(),
  createdUsername: "Vitor",
  updatedDate: new Date(),
  updatedUsername: "Maria",
};

/**
 * CadastroHabilidadeModal
 */

type Props = {
  open: boolean,
  saving: boolean,
  selectedHabilidade: AvaliacaoHabilidade | undefined,
  funcionarios: FuncionarioDropDown[],
  habilidadeItems: HabilidadeItem[],
  habilidadeItemsPage: number,
  habilidadeItemsRowsPerPage: number,
  habilidadeItemsTotalSize: number,
  onSubmit: (form: AvaliacaoHabilidadeForm) => void,
  onOpenChangeHistory: (id: number, tabelaTipo: LogTipoTabela) => void,
  onClose: () => void,
  onCadastrarHabilidadeItem: () => void,
  onHabilidadeItemsPageChange: (page: number) => void,
  onEditActionHabilidadeItem: (idHabilidade: number | null, openState: boolean) => void,
  onDeleteActionHabilidadeItem: (idHabilidade: number | null, openState: boolean) => void,
};

export const CadastroHabilidadeModal = ({
  open,
  saving,
  selectedHabilidade,
  funcionarios,
  habilidadeItems,
  habilidadeItemsPage,
  habilidadeItemsRowsPerPage,
  habilidadeItemsTotalSize,
  onSubmit,
  onOpenChangeHistory,
  onClose,
  onCadastrarHabilidadeItem,
  onHabilidadeItemsPageChange,
  onEditActionHabilidadeItem,
  onDeleteActionHabilidadeItem,
}: Props) => {
  const hookForm = useForm<AvaliacaoHabilidadeForm>({
    mode: "onSubmit",
    reValidateMode: "onSubmit",
    resolver: zodResolver(AvaliacaoHabilidadeSchema),
  });

  const [formHabilidadeItems] = hookForm.watch(['habilidadeItems']);

  useEffect(() => {
    if (open) {
      if (selectedHabilidade) {
        hookForm.setValue('descricao', selectedHabilidade.descricao);
        hookForm.setValue('data', selectedHabilidade.dataAvaliacao ?? new Date())
        hookForm.setValue('dataReavaliacao', selectedHabilidade.dataReavaliacao);
        hookForm.setValue('observacao', selectedHabilidade.observacao);

        if (selectedHabilidade.idAvaliadoPor) {
          hookForm.setValue('idAvaliadoPor', selectedHabilidade.idAvaliadoPor);
        } else {
          hookForm.resetField('idAvaliadoPor', undefined);
        }
      } else {
        hookForm.reset();
      }

      if (habilidadeItems) {
        if (habilidadeItems.length > 0) {
          hookForm.setValue('habilidadeItems', habilidadeItems.map((item) => ({
            idHabilidadeItem: item.idHabilidadeItem,
            idHabilidade: item.idHabilidade,
            habilidade: item.habilidade,
            notaSelecionada: item.nota,
            nota: item.nota,
          })));
        } else {
          hookForm.setValue('habilidadeItems', []);
        }
      }
    }
  }, [
    hookForm,
    open,
    selectedHabilidade,
    habilidadeItems,
  ]);

  const columns = useMemo(() => {
    return GenerateHabilidadeItemsColumnsDefinition({
      onChangeHabilidadeItem: (idHabilidadeItem, nota) => {
        const habilidadeItemsValue = hookForm.getValues('habilidadeItems');
        const desiredId = habilidadeItemsValue.findIndex((item) => item.idHabilidadeItem === idHabilidadeItem);

        if (desiredId >= 0) {
          habilidadeItemsValue[desiredId].notaSelecionada = nota;
          hookForm.setValue('habilidadeItems', [...habilidadeItemsValue]);
        }
      },
      onEditar: (idHabilidadeItem) => {
        onEditActionHabilidadeItem(idHabilidadeItem, true)
      },
      onDeletar: (idHabilidadeItem) => {
        onDeleteActionHabilidadeItem(idHabilidadeItem, true)
      },
    });
  }, [
    hookForm,
    onEditActionHabilidadeItem,
    onDeleteActionHabilidadeItem,
  ]);

  const handleCadastrarHabilidadeItem = useCallback(() => {
    if (!saving) onCadastrarHabilidadeItem();
  }, [
    saving,
    onCadastrarHabilidadeItem,
  ]);

  const handleSubmit = useCallback((form: AvaliacaoHabilidadeForm) => {
    if (!saving) onSubmit(form);
  }, [
    saving,
    onSubmit,
  ]);

  const handleClose = useCallback(() => {
    if (!saving) onClose();
  }, [
    saving,
    onClose,
  ]);

  const handleChangeHistory = useCallback(() => {
    if (selectedHabilidade) {
      onOpenChangeHistory(selectedHabilidade.idAvaliacaoHabilidade, LogTipoTabela.AVALIACAO_HABILIDADE);
    }
  }, [selectedHabilidade, onOpenChangeHistory]);

  return (
    <Dialog
      open={open}
      slotProps={{
        paper: {
          component: 'form',
          onSubmit: hookForm.handleSubmit(handleSubmit),
        }
      }}
      sx={{
        "& .MuiDialog-container": {
          "& .MuiPaper-root": {
            width: "100%",
            maxWidth: "60rem",
          },
        },
      }}>
      <DialogTitle>
        {selectedHabilidade ? "Editar" : "Cadastrar"} habilidade
      </DialogTitle>

      <DialogContent>
        <ModalLoading loading={saving}>
          <ModalContainer changeLogData={selectedHabilidade && changeLogData}>
            <FormContainer title={"Avaliação"} arrangement="fullRow">
              <>
                <DefaultFormInput
                  name='descricao'
                  label='Descrição'
                  readOnly={saving}
                  reactHookForm={hookForm} />

                <FormDatePickerInput
                  name='data'
                  label='Data'
                  readOnly={saving}
                  required={true}
                  reactHookForm={hookForm} />

                <DefaultFormAutocomplete
                  name='idAvaliadoPor'
                  label='Avaliado Por'
                  required={true}
                  readOnly={saving}
                  options={funcionarios}
                  getOptionLabel={(opt: FuncionarioDropDown) => opt.nome}
                  getOptionKey={(opt: FuncionarioDropDown) => opt.idFuncionario}
                  reactHookForm={hookForm} />

                <FormDatePickerInput
                  name='dataReavaliacao'
                  label='Data da Reavaliação'
                  readOnly={saving}
                  reactHookForm={hookForm} />

                <DefaultFormInput
                  name={'observacao'}
                  label={"Observações"}
                  readOnly={saving}
                  multiline={true}
                  rows={7}
                  reactHookForm={hookForm}
                />
              </>
            </FormContainer>

            <div className="flex flex-col w-full">
              <div className="flex flex-row justify-between items-center mt-8 mb-4">
                <p className="text-lg">
                  Habilidades Avaliadas
                </p>
                <div>
                  <PrimaryButton
                    startIcon={<DynamicIcon nameIcon="AddRounded" size={25} className="text-white" />}
                    onClick={handleCadastrarHabilidadeItem}
                    buttonColor="primary-green">
                    Novo item
                  </PrimaryButton>
                </div>
              </div>

              <StyledDataGrid
                tableName="cadastro_habilidade_modal"
                disableColumnMenu={false}
                disableRowSelectionOnClick={true}
                getRowId={(row: AvaliacaoHabilidadeItemForm) => row.idHabilidadeItem}
                columns={columns}
                loading={saving}
                rows={formHabilidadeItems ?? []}
                rowCount={formHabilidadeItems?.length ?? 0}
                height='35vh'
                pageSizeOptions={[25]}
                paginationMode="server"
                paginationModel={{
                  page: habilidadeItemsPage,
                  pageSize: habilidadeItemsRowsPerPage,
                }} />

              <div className="flex flex-row justify-between items-center">
                <p>
                  TOTAL DE PONTOS EM PERCENTUAL:
                  <span className="ms-4 font-bold">
                    {typeof selectedHabilidade?.totalPontos === 'number' && selectedHabilidade?.totalPontos?.toFixed(2) + '%'}
                  </span>
                </p>

                <TablePagination
                  component="div"
                  count={habilidadeItemsTotalSize}
                  page={habilidadeItemsPage - 1}
                  rowsPerPageOptions={[25]}
                  onPageChange={(_, p) => onHabilidadeItemsPageChange(p)}
                  rowsPerPage={habilidadeItemsRowsPerPage} />

              </div>

              <div className="w-full text-xs border border-grey-100 border-l-8 border-orange-500 mb-4">
                <List dense={true}>
                  <ListItem>De 0 a 40% dos pontos, avaliar a manutenção do funcionário na equipe.</ListItem>
                  <ListItem>De 41 a 65% dos pontos, funcionário deverá passar por reciclagem.</ListItem>
                  <ListItem>De 66 a 100% dos pontos, funcionário com desempenho satisfatório.</ListItem>
                </List>

              </div>
            </div>
          </ModalContainer>
        </ModalLoading>
      </DialogContent>

      <DialogActions>
        <div className="w-full h-full items-center justify-between flex p-4 gap-4">
          <div>
            {
              isNotNullOrUndefined(selectedHabilidade) &&
              <PrimaryButton
                buttonColor="yellow"
                onClick={handleChangeHistory}>
                <span className="text-xs">
                  Historico de alterações
                </span>
              </PrimaryButton>
            }
          </div>

          <div className="flex flex-row gap-2">
            <div className="p-2 hover:bg-hover-light-gray cursor-pointer dark:hover:bg-border-dark-gray rounded-[4px] font-semibold">
              <h1
                className="text-sm text-black dark:text-white"
                onClick={handleClose}>
                Cancelar
              </h1>
            </div>

            <div className="w-[120px]">
              {saving ? (
                <PrimaryButton
                  buttonColor="primary-green">
                  Salvando...
                </PrimaryButton>
              ) : (
                <PrimaryButton
                  type="submit"
                  buttonColor="primary-green">
                  Salvar
                </PrimaryButton>
              )}
            </div>
          </div>
        </div>
      </DialogActions>
    </Dialog >
  );
}